import { chapterCache } from './storage.js';
import { loadChapter } from './api.js';

const REF_RE = /^\s*((?:[1-3]\s?)?[A-Za-z][A-Za-z\s]*?)\.?\s*(\d+)?\s*(?::\s*(\d+))?\s*$/;

function normalize(str) {
	return String(str).toLowerCase().replace(/\s+/g, '');
}

function findBook(index, needle) {
	const n = normalize(needle);
	if (!n) return null;

	let match = index.books.find((b) => normalize(b.id) === n || normalize(b.name) === n);
	if (match) return match;

	match = index.books.find(
		(b) => normalize(b.abbrev) === n || (b.aliases || []).some((a) => normalize(a) === n)
	);
	if (match) return match;

	// Fall back to "starts with" so partial typing ("gen", "jo") still resolves.
	match = index.books.find((b) => normalize(b.name).startsWith(n) || normalize(b.id).startsWith(n));
	return match || null;
}

/**
 * Parses strings like "John 3:16", "Gen 1", "1 John 2" into a navigable
 * { book, chapter, verse } reference, or returns null if the query doesn't
 * look like a scripture reference at all.
 */
export function parseReference(query, index) {
	if (!index || !index.books) return null;

	const m = REF_RE.exec(query);
	if (!m || !m[1]) return null;

	const book = findBook(index, m[1]);
	if (!book) return null;

	const chapter = m[2] ? parseInt(m[2], 10) : 1;
	const verse = m[3] ? parseInt(m[3], 10) : null;

	if (chapter < 1 || chapter > book.chapters) return null;

	return { book: book.id, bookName: book.name, chapter, verse };
}

/**
 * Keyword search across every chapter of the given translation currently
 * cached in IndexedDB (i.e. chapters the reader has already opened).
 */
export async function searchCached(query, translation) {
	const q = query.trim().toLowerCase();
	if (!q) return [];

	const chapters = (await chapterCache.allCached()).filter((c) => c.translation === translation);
	const results = [];

	for (const chapterData of chapters) {
		for (const v of chapterData.verses) {
			if (v.text.toLowerCase().includes(q)) {
				results.push({
					book: chapterData.book,
					bookName: chapterData.bookName,
					chapter: chapterData.chapter,
					verse: v.verse,
					text: v.text,
				});
			}
		}
	}

	return results;
}

/**
 * Progressive "deep search": walks the full book/chapter index fetching
 * (and caching) chapters on demand, invoking onMatch as results are found
 * and onProgress as it moves through the dataset. Returns a cancel() fn.
 */
export function deepSearch(dataUrl, index, translation, query, offlineCacheEnabled, { onMatch, onProgress, onDone }) {
	const q = query.trim().toLowerCase();
	let cancelled = false;

	(async () => {
		let scanned = 0;
		const total = index.books.reduce((sum, b) => sum + b.chapters, 0);

		for (const book of index.books) {
			for (let chapter = 1; chapter <= book.chapters; chapter++) {
				if (cancelled) return;

				scanned++;
				if (onProgress) onProgress(scanned, total);

				const { data } = await loadChapter(dataUrl, translation, book.id, chapter, offlineCacheEnabled);
				if (!data) continue;

				for (const v of data.verses) {
					if (v.text.toLowerCase().includes(q)) {
						onMatch({
							book: data.book,
							bookName: data.bookName,
							chapter: data.chapter,
							verse: v.verse,
							text: v.text,
						});
					}
				}
			}
		}

		if (onDone) onDone();
	})();

	return () => {
		cancelled = true;
	};
}
